import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export interface ProductFormData {
  name: string;
  image: string; // base64 without prefix
  description: string;
  price: string;
  currency: string;
  url: string;
  retailerId: string;
}

interface ProductFormModalProps {
  isOpen: boolean;
  isEdit: boolean;
  initialData?: Partial<ProductFormData>;
  loading: boolean;
  onClose: () => void;
  onSubmit: (data: ProductFormData) => void;
}

export default function ProductFormModal({
  isOpen,
  isEdit,
  initialData,
  loading,
  onClose,
  onSubmit,
}: ProductFormModalProps) {
  const { t } = useTranslation("dashboard");
  const [form, setForm] = useState<ProductFormData>({
    name: initialData?.name || "",
    image: initialData?.image || "",
    description: initialData?.description || "",
    price: initialData?.price || "",
    currency: initialData?.currency || "EGP",
    url: initialData?.url || "",
    retailerId: initialData?.retailerId || "",
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialData?.image ? `data:image/jpeg;base64,${initialData.image}` : null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const base64 = await fileToBase64(file);
        setForm((prev) => ({ ...prev, image: base64 }));
        setPreviewUrl(URL.createObjectURL(file));
      } catch {
        // ignore
      }
    },
    [],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit(form);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/60">
          <h3 className="font-heading text-base text-foreground-950">
            {isEdit ? t("catalog.editProduct") : t("catalog.addProduct")}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1">
              {t("catalog.productName")} *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t("catalog.productNamePlaceholder")}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1">
              {isEdit ? t("catalog.replaceImage") : t("catalog.productImage")} *
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-40 rounded-xl border-2 border-dashed border-background-200 bg-background-100/50 flex flex-col items-center justify-center cursor-pointer hover:bg-background-100 transition-colors overflow-hidden"
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <>
                  <i className="ri-image-add-line text-2xl text-foreground-400 mb-2"></i>
                  <span className="text-xs text-foreground-500">{t("catalog.clickToUpload")}</span>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
            {isEdit && form.image && (
              <p className="text-xs text-foreground-400 mt-1">{t("catalog.leaveEmptyToKeep")}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1">
              {t("catalog.description")}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={t("catalog.descriptionPlaceholder")}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1">
                {t("catalog.price")}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm((prev) => ({ ...prev, price: e.target.value }))}
                placeholder={t("catalog.pricePlaceholder")}
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1">
                {t("catalog.currency")}
              </label>
              <input
                type="text"
                value={form.currency}
                onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))}
                placeholder="EGP"
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1">
              {t("catalog.productUrl")}
            </label>
            <input
              type="url"
              value={form.url}
              onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
              placeholder={t("catalog.productUrlPlaceholder")}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1">
              {t("catalog.retailerId")}
            </label>
            <input
              type="text"
              value={form.retailerId}
              onChange={(e) => setForm((prev) => ({ ...prev, retailerId: e.target.value }))}
              placeholder={t("catalog.retailerIdPlaceholder")}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("catalog.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading || !form.name.trim() || (!isEdit && !form.image)}
              className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {loading && <i className="ri-loader-4-line animate-spin text-sm"></i>}
              {isEdit ? t("catalog.saveChanges") : t("catalog.addProductBtn")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}